/* eslint-disable max-len */
/* eslint-disable import/prefer-default-export */
import { formatRoute } from 'react-router-named-routes';
import React from 'react';
// global variables
import { rules } from 'config';
// our components
import { AD, REVIEW_RULES } from 'routes/paths';

/**
 * @description
 * Reduce rule array to string
 *
 * @param {Array} entry - Rules.
 * @returns {string} Reduced rules.
 */
const reduceRules = entry =>
  entry.reduce(
    (acc, rule) => acc.concat(`${rule.group}: ${rule.value} <br/>`),
    '',
  );

/**
 * @description
 * Helper to render similarity rules
 *
 * @param {string} id - Base ad id.
 * @param {Object} classes - Styles.
 * @param {string} ruleName - Rule name.
 * @param {Object} matchedRule - Element.
 * @param {string} target - Target rule.
 */
const renderSimilarityRules = (id, classes, ruleName, matchedRule, target) => (
  <a
    key={`mr-similarity-${id}`}
    className={['read', classes.adBtns, classes.titleBtn].join(' ')}
    href={`${formatRoute(REVIEW_RULES.DETAILS, {
      id,
      rule: rules.SIMILARITY,
    })}`}
    rel="noopener noreferrer"
    target="_blank"
    style={{ float: 'left', width: '100%' }}
  >
    {`${ruleName} (${`${matchedRule[target].score}`.substr(0, 4)})`}
  </a>
);

/**
 * @description
 * Helper to render similarity reinsert rules
 *
 * @param {string} id - Base ad id.
 * @param {Object} classes - Styles.
 * @param {string} ruleName - Rule name.
 * @param {Object} matchedRule - Element.
 * @param {string} target - Target rule.
 */
const renderSimilarityReinsertRules = (
  id,
  classes,
  ruleName,
  matchedRule,
  target,
) => (
  <a
    key={`mr-similarity-reinsert-${id}`}
    className={['read', classes.adBtns, classes.titleBtn].join(' ')}
    href={`${formatRoute(REVIEW_RULES.DETAILS, {
      id,
      rule: rules.SIMILARITY_REINSERT,
    })}`}
    rel="noopener noreferrer"
    target="_blank"
    style={{ float: 'left', width: '100%' }}
  >
    {`${ruleName} (${`${matchedRule[target].score}`.substr(0, 4)})`}
  </a>
);

/**
 * @description
 * Helper to render bot score rules
 *
 * @param {string} id - Base ad id.
 * @param {Object} classes - Styles.
 * @param {string} ruleName - Rule name.
 * @param {Object} matchedRule - Element.
 * @param {string} target - Target rule.
 */
const renderBotScoreRules = (id, classes, ruleName, matchedRule, target) => (
  <a
    key={`mr-botscore-${id}`}
    className={['read', classes.adBtns, classes.titleBtn].join(' ')}
    href={`${AD.LIST}?sameOwnerAds=${id}`}
    rel="noopener noreferrer"
    target="_blank"
    style={{ float: 'left', width: '100%' }}
  >
    {`${ruleName}: ${
      matchedRule[target] === null
        ? 'Null'
        : `(${`${matchedRule[target]}`.substr(0, 3)})`
    }`}
  </a>
);

/**
 * @description
 * Helper to render bot score related rules
 *
 * @param {string} id - Base ad id.
 * @param {Object} classes - Styles.
 * @param {string} ruleName - Rule name.
 * @param {Object} matchedRule - Element.
 * @param {string} target - Target rule.
 */
const renderBotScoreRelatedRules = (
  id,
  classes,
  ruleName,
  matchedRule,
  target,
) => (
  <a
    key={`mr-botscore-related-${id}`}
    className={['read', classes.adBtns, classes.titleBtn].join(' ')}
    href={`${formatRoute(REVIEW_RULES.DETAILS, {
      id,
      rule: rules.BOT_SCORE_RELATED,
    })}`}
    rel="noopener noreferrer"
    target="_blank"
    style={{ float: 'left', width: '100%' }}
  >
    {`${ruleName}: ${`${matchedRule[target].score}`.substr(
      0,
      4,
    )} ${`${matchedRule[target].match_percent}`.substr(0, 4)}% ${
      matchedRule[target].ads_count
    }`}
  </a>
);

/**
 * @description
 * Helper to render exact match rules
 *
 * @param {string} id - Base ad id.
 * @param {Object} classes - Styles.
 * @param {string} ruleName - Rule name.
 * @param {Object} matchedRule - Element.
 * @param {string} target - Target rule.
 */
const renderExactMatch = (id, classes, ruleName, matchedRule, target) => (
  <a
    key={`mr-exatcmatch-${id}`}
    className={['read', classes.adBtns, classes.titleBtn].join(' ')}
    href={formatRoute(AD.DETAILS, { id: matchedRule[target].ad })}
    rel="noopener noreferrer"
    target="_blank"
    style={{ float: 'left', width: '100%' }}
  >
    {`${ruleName} ${matchedRule[target].ad}`}
  </a>
);

/**
 * @description
 * Helper to render ip rate limit rules
 *
 * @param {string} id - Base ad id.
 * @param {Object} classes - Styles.
 * @param {string} ruleName - Rule name.
 * @param {Object} matchedRule - Element.
 * @param {string} target - Target rule.
 * @returns {*} Jsx component.
 */
const renderIpRateLimitRules = (id, classes, ruleName, matchedRule, target) => {
  // handle backward compatibility
  const adsCount =
    typeof matchedRule[target] === 'object'
      ? matchedRule[target].count
      : matchedRule[target];
  return (
    <a
      key={`mr-iprate-${id}`}
      className={['read', classes.adBtns, classes.titleBtn].join(' ')}
      href={`${formatRoute(REVIEW_RULES.DETAILS, {
        id,
        rule: rules.IP_RATE_LIMIT,
      })}`}
      rel="noopener noreferrer"
      target="_blank"
      style={{ float: 'left', width: '100%' }}
    >
      <span
        className={classes.onlyInfoRules}
      >{`${ruleName} (${adsCount})`}</span>
    </a>
  );
};

/**
 * @description
 * Helper to render flagged frequency rules
 *
 * @param {string} id - Base ad id.
 * @param {Object} classes - Styles.
 * @param {string} ruleName - Rule name.
 * @param {Object} matchedRule - Element.
 * @param {string} target - Target rule.
 * @returns {*} Jsx component.
 */
const renderFlaggedFrequencyRules = (
  id,
  classes,
  ruleName,
  matchedRule,
  target,
) => (
  <React.Fragment>
    <a
      key={`mr-flagged-frequency-${id}`}
      className={['read', classes.adBtns, classes.titleBtn].join(' ')}
      href={`${formatRoute(REVIEW_RULES.DETAILS, {
        id,
        rule: rules.FLAGGED_FREQUENCY,
      })}`}
      rel="noopener noreferrer"
      target="_blank"
      style={{ width: '100%' }}
    >
      {`${ruleName}`}
    </a>
    {Object.keys(matchedRule[target]).map(key => (
      <p
        style={{ marginBottom: '0px' }}
      >{`${key}: ${matchedRule[target][key]}`}</p>
    ))}
  </React.Fragment>
);

/**
 * @description
 * Helper to render flagged count rules
 *
 * @param {string} id - Base ad id.
 * @param {Object} classes - Styles.
 * @param {string} ruleName - Rule name.
 * @param {Object} matchedRule - Element.
 * @param {string} target - Target rule.
 * @returns {*} Jsx component.
 */
const renderFlaggedCountRules = (
  id,
  classes,
  ruleName,
  matchedRule,
  target,
) => (
  <React.Fragment>
    <a
      key={`mr-flagged-count-${id}`}
      className={['read', classes.adBtns, classes.titleBtn].join(' ')}
      href={`${formatRoute(REVIEW_RULES.DETAILS, {
        id,
        rule: rules.FLAGGED_COUNT,
      })}`}
      rel="noopener noreferrer"
      target="_blank"
      style={{ width: '100%' }}
    >
      {`${ruleName}`}
    </a>
    {Object.keys(matchedRule[target]).map(key => (
      <p
        style={{ marginBottom: '0px' }}
      >{`${key}: ${matchedRule[target][key]}`}</p>
    ))}
  </React.Fragment>
);

/**
 * @description
 * Helper to render duplicated images rules
 *
 * @param {string} id - Base ad id.
 * @param {Object} classes - Styles.
 * @param {string} ruleName - Rule name.
 * @param {Object} matchedRule - Element.
 * @param {string} target - Target rule.
 */
const renderDuplicatedImages = (id, classes, ruleName, matchedRule, target) => (
  <React.Fragment>
    <a
      key={`mr-duplicated-count-${id}`}
      className={['read', classes.adBtns, classes.titleBtn].join(' ')}
      href={`${formatRoute(REVIEW_RULES.DETAILS, {
        id: [id, ...matchedRule[target].ads],
        rule: rules.DUPLICATED_IMAGES,
      })}`}
      rel="noopener noreferrer"
      target="_blank"
      style={{ width: '100%' }}
    >
      {`${ruleName}`}
    </a>
    {Object.keys(matchedRule[target]).map(key => (
      <p
        style={{ marginBottom: '0px' }}
      >{`${key}: ${matchedRule[target][key].length}`}</p>
    ))}
  </React.Fragment>
);

/**
 * @description
 * Helper to render user rate limit rules
 *
 * @param {string} id - Base ad id.
 * @param {Object} classes - Styles.
 * @param {string} ruleName - Rule name.
 * @param {Object} matchedRule - Element.
 * @param {string} target - Target rule.
 */
const renderUserRateLimitRules = (
  id,
  classes,
  ruleName,
  matchedRule,
  target,
) => (
  <a
    key={`mr-user-rate-limit-${id}`}
    className={['read', classes.adBtns, classes.titleBtn].join(' ')}
    href={`${formatRoute(REVIEW_RULES.DETAILS, {
      id,
      rule: rules.USER_RATE_LIMIT,
    })}`}
    rel="noopener noreferrer"
    target="_blank"
    style={{ float: 'left', width: '100%' }}
  >
    {`${ruleName} (${matchedRule[target].count})`}
  </a>
);

/**
 * @description
 * Get suggested category title from review category rule payload.
 *
 * @param {*} reviewCategory - Rule payload.
 * @returns {string} Suggested category title.
 */
const getReviewCategoryTitle = reviewCategory => {
  if (!reviewCategory) {
    return '';
  }
  if (typeof reviewCategory === 'string') {
    return reviewCategory;
  }
  if (typeof reviewCategory === 'object') {
    return (
      reviewCategory.category ||
      reviewCategory.category_title ||
      reviewCategory.subcategory ||
      reviewCategory.subcategory_title ||
      ''
    );
  }
  return '';
};

/**
 * @description
 * Helper to render review category rule
 *
 * @param {string} id - Base ad id.
 * @param {Object} classes - Styles.
 * @param {string} ruleName - Rule name.
 * @param {Object} matchedRule - Element.
 * @param {string} target - Target rule.
 * @returns {*} Jsx component.
 */
const renderReviewCategoryRule = (
  id,
  classes,
  ruleName,
  matchedRule,
  target,
) => {
  const suggestedCategory = getReviewCategoryTitle(matchedRule[target]);
  return (
    <a
      key={`mr-review-category-${id}`}
      className={['read', classes.adBtns, classes.titleBtn].join(' ')}
      href={`${formatRoute(REVIEW_RULES.DETAILS, {
        id,
        rule: rules.REVIEW_CATEGORY,
      })}`}
      rel="noopener noreferrer"
      target="_blank"
      style={{ float: 'left', width: '100%' }}
    >
      {ruleName}
      {suggestedCategory && (
        <React.Fragment>
          <br />
          {`(${suggestedCategory})`}
        </React.Fragment>
      )}
    </a>
  );
};

/**
 * @description
 * Helper to know if current rule is from list.
 *
 * @param {string} matchedRule - Specific rule.
 * @returns {boolean} From list or not.
 */
export function isFromList(matchedRule) {
  const rule = Object.keys(matchedRule)[0];
  if (
    rule === 'allow_ip' ||
    rule === 'block_ip' ||
    rule === 'review_ip' ||
    rule === 'allow_email' ||
    rule === 'block_email' ||
    rule === 'review_email' ||
    rule === 'allow_phone' ||
    rule === 'block_phone' ||
    rule === 'review_phone' ||
    rule === 'block_phrases' ||
    rule === 'review_phrases' ||
    rule === 'review_unpublished'
  )
    return true;
  return false;
}

/**
 * @description
 * Render matchedRules column item
 *
 * @param {string} node - Ad.
 * @param {Object} classes - Styles.
 * @param {Object} t - Translate.
 * @param {Object} matchedRule - Element.
 * @returns {string} Table rows item.
 */
export default function matchedRules(node, classes, t, matchedRule) {
  const keys = Object.keys(matchedRule);
  const target = keys[0];

  let renderItem = '';

  if (target) {
    const ruleName = t(`review_queue.rules.${target}`);

    switch (target) {
      case 'allow_ip': {
        renderItem = `<h6>${ruleName}</h6> ${reduceRules(matchedRule[target])}`;
        break;
      }
      case 'block_ip': {
        renderItem = `<h6>${ruleName}</h6>  ${reduceRules(
          matchedRule[target],
        )}`;
        break;
      }
      case 'review_ip': {
        renderItem = `<h6>${ruleName}</h6>  ${reduceRules(
          matchedRule[target],
        )}`;
        break;
      }
      case 'allow_email': {
        renderItem = `<h6>${ruleName}</h6>  ${reduceRules(
          matchedRule[target],
        )}`;
        break;
      }
      case 'block_email': {
        renderItem = `<h6>${ruleName}</h6>  ${reduceRules(
          matchedRule[target],
        )}`;
        break;
      }
      case 'review_email': {
        renderItem = `<h6>${ruleName}</h6>  ${reduceRules(
          matchedRule[target],
        )}`;
        break;
      }
      case 'allow_phone': {
        renderItem = `<h6>${ruleName}</h6>  ${reduceRules(
          matchedRule[target],
        )}`;
        break;
      }
      case 'block_phone': {
        renderItem = `<h6>${ruleName}</h6>  ${reduceRules(
          matchedRule[target],
        )}`;
        break;
      }
      case 'review_phone': {
        renderItem = `<h6>${ruleName}</h6>  ${reduceRules(
          matchedRule[target],
        )}`;
        break;
      }
      case 'block_phrases': {
        renderItem = `<h6>${ruleName}</h6>  ${reduceRules(
          matchedRule[target],
        )}`;
        break;
      }
      case 'review_phrases': {
        renderItem = `<h6>${ruleName}</h6>  ${reduceRules(
          matchedRule[target],
        )}`;
        break;
      }
      case 'review_unpublished': {
        renderItem = `<h6>${ruleName}</h6>  ${t(
          `review_queue.rule_values.${matchedRule[target]}`,
        )}`;
        break;
      }
      case 'block_bot_score': {
        renderItem = renderBotScoreRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_bot_score_related': {
        renderItem = renderBotScoreRelatedRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_bot_score_related': {
        renderItem = renderBotScoreRelatedRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_exact_match': {
        renderItem = renderExactMatch(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_exact_match_reinsert': {
        renderItem = renderExactMatch(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_similarity_score': {
        renderItem = renderSimilarityRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_similarity_score_reinsert': {
        renderItem = renderSimilarityReinsertRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_similarity_score': {
        renderItem = renderSimilarityRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_similarity_score_reinsert': {
        renderItem = renderSimilarityReinsertRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_bot_score': {
        renderItem = renderBotScoreRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_ip_rate_limit': {
        renderItem = renderIpRateLimitRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_ip_rate_limit': {
        renderItem = (
          <span
            className={classes.onlyInfoRules}
          >{`${ruleName} (${matchedRule[target]})`}</span>
        );
        break;
      }
      case 'review_flagged_frequency': {
        renderItem = renderFlaggedFrequencyRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_flagged_frequency': {
        renderItem = renderFlaggedFrequencyRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_flagged_count': {
        renderItem = renderFlaggedCountRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_flagged_count': {
        renderItem = renderFlaggedCountRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_duplicated_images': {
        renderItem = renderDuplicatedImages(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_duplicated_images': {
        renderItem = renderExactMatch(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_duplicated_images_reinsert': {
        renderItem = renderDuplicatedImages(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_duplicated_images_reinsert': {
        renderItem = renderExactMatch(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_duplicated_main_image': {
        renderItem = renderDuplicatedImages(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_user_insert_rate_limit': {
        renderItem = renderUserRateLimitRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_user_update_ads_rate_limit': {
        renderItem = renderUserRateLimitRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'block_user_update_ad_rate_limit': {
        renderItem = renderUserRateLimitRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_user_insert_rate_limit': {
        renderItem = renderUserRateLimitRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_user_update_ads_rate_limit': {
        renderItem = renderUserRateLimitRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_user_update_ad_rate_limit': {
        renderItem = renderUserRateLimitRules(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      case 'review_category': {
        renderItem = renderReviewCategoryRule(
          node.id,
          classes,
          ruleName,
          matchedRule,
          target,
        );
        break;
      }
      default: {
        renderItem = ruleName;
      }
    }
  }
  return renderItem;
}
